import React, { useState } from 'react';
import { 
  Plus, CheckCircle2, AlertCircle, Send, Share2, 
  Search, Shield, Rocket, Check, Copy 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export const RequestPoolPage = () => {
  const [formData, setFormData] = useState({
    projectName: "",
    description: "",
    discordUrl: "",
    twitterHandle: "",
    totalSupply: "",
    estimatedHolders: "",
    rugDate: "",
    contactEmail: "",
    additionalInfo: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const shareableLink = `${window.location.origin}/request?ref=${formData.projectName?.replace(/\s+/g, '-').toLowerCase() || 'community'}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareableLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="bg-gradient-to-br from-green-500/10 to-purple-500/10 border-green-500/20">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Request Submitted!</h2>
            <p className="text-text-secondary mb-6">
              Thank you for submitting your project. Our team will review your request and get back to you within 48 hours.
            </p>
            <div className="bg-surface-2 rounded-xl p-4 mb-6">
              <p className="text-text-secondary text-sm mb-2">Share this link with your community:</p>
              <div className="flex items-center gap-2">
                <Input value={shareableLink} readOnly className="bg-white/5 border-white/10 text-white text-sm" />
                <Button onClick={copyLink} variant="outline" className="border-white/20 text-white">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <Button onClick={() => setSubmitted(false)} variant="outline" className="border-secondary-theme/30 text-secondary-theme">
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-theme to-secondary-theme flex items-center justify-center mx-auto mb-4">
          <Plus className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Request Your Project</h1>
        <p className="text-text-secondary max-w-lg mx-auto">
          Are you a community leader of a rugged project? Submit your project to be added to BURNZ Union 
          and help your community recover together.
        </p>
      </div>

      {/* Info Banner */}
      <Card className="bg-primary-theme/10 border-primary-theme/20">
        <CardContent className="p-4 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-primary-theme flex-shrink-0" />
          <div>
            <h3 className="text-primary-theme font-semibold mb-1">For Community Leaders</h3>
            <p className="text-text-secondary text-sm">
              This form is for verified community leaders who want to bring their rugged project community to BURNZ Union. 
              Once approved, your community members can join, dump their NFTs, earn XP, and participate in marketing quests together.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <Card className="bg-surface-2 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Project Information</CardTitle>
          <CardDescription className="text-text-secondary">Fill out the details below to request your project be added</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-text-secondary text-sm block mb-2">Project Name *</label>
                <Input 
                  required
                  placeholder="e.g., Neon Apes Genesis"
                  value={formData.projectName}
                  onChange={(e) => setFormData({...formData, projectName: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="text-text-secondary text-sm block mb-2">Project Description *</label>
                <textarea 
                  required
                  placeholder="Brief description of your project and what happened..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full h-24 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-text-muted focus:border-secondary-theme/50 outline-none resize-none"
                />
              </div>

              <div>
                <label className="text-text-secondary text-sm block mb-2">Discord Server URL</label>
                <Input 
                  placeholder="https://discord.gg/..."
                  value={formData.discordUrl}
                  onChange={(e) => setFormData({...formData, discordUrl: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <label className="text-text-secondary text-sm block mb-2">Twitter/X Handle</label>
                <Input 
                  placeholder="@projecthandle"
                  value={formData.twitterHandle}
                  onChange={(e) => setFormData({...formData, twitterHandle: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <label className="text-text-secondary text-sm block mb-2">Total NFT Supply</label>
                <Input 
                  type="number"
                  placeholder="e.g., 10000"
                  value={formData.totalSupply}
                  onChange={(e) => setFormData({...formData, totalSupply: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <label className="text-text-secondary text-sm block mb-2">Estimated Holders</label>
                <Input 
                  type="number"
                  placeholder="e.g., 2500"
                  value={formData.estimatedHolders}
                  onChange={(e) => setFormData({...formData, estimatedHolders: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <label className="text-text-secondary text-sm block mb-2">Approximate Rug Date</label>
                <Input 
                  type="date"
                  value={formData.rugDate}
                  onChange={(e) => setFormData({...formData, rugDate: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <label className="text-text-secondary text-sm block mb-2">Contact Email *</label>
                <Input 
                  required
                  type="email"
                  placeholder="leader@email.com"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-text-secondary text-sm block mb-2">Additional Information</label>
                <textarea 
                  placeholder="Any additional context about your community, recovery efforts, etc..."
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
                  className="w-full h-20 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-text-muted focus:border-secondary-theme/50 outline-none resize-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4">
              <Button type="submit" className="flex-1 bg-primary-theme hover:opacity-90 text-white h-12">
                <Send className="w-4 h-4 mr-2" />Submit Request
              </Button>
              <Button type="button" variant="outline" className="border-white/20 text-white" onClick={copyLink}>
                <Share2 className="w-4 h-4 mr-2" />Share Form
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* What Happens Next */}
      <Card className="bg-surface-2 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-lg">What Happens Next?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { step: 1, title: "Review", desc: "Our team reviews your submission within 48 hours", icon: Search },
              { step: 2, title: "Verification", desc: "We verify community details and leader authenticity", icon: Shield },
              { step: 3, title: "Launch", desc: "Your pool goes live and community can start earning", icon: Rocket },
            ].map(item => (
              <div key={item.step} className="text-center p-4 bg-white/5 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-secondary-theme/20 flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-5 h-5 text-secondary-theme" />
                </div>
                <p className="text-white font-medium text-sm">{item.title}</p>
                <p className="text-text-muted text-xs mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RequestPoolPage;
